import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import MetaTags from '../../components/MetaTags';
import Footer from '../../components/Footer/FooterMenu';
import { CustomButton } from '../../components/Buttons';

import { useUserDetails } from '../../services/communityService';
import { callBackbonePostApi } from '../../services/backboneApiService';
import { getUser } from '../../utils/cookie';

class BattleBreakdownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('BattleBreakdown Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ 
          minHeight: '100vh', 
          backgroundColor: '#1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography sx={{ color: '#fff', textAlign: 'center' }}>
            Something went wrong loading the battle breakdown. Please try again.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}



const BattleBreakdown = () => {
  const { username, sessionId } = useParams();
  const navigate = useNavigate();
  const currentUser = getUser();
  const {
    data: { data: userDetails, isLoading: isLoadingUserDetails },
  } = useUserDetails(username, null);
  const canGiveFeedback =
    !!currentUser?.userId &&
    !!userDetails?.results?.user_id &&
    String(currentUser.userId) === String(userDetails.results.user_id);
  const [isRecapLoading, setIsLoading] = useState(true);
  const [battleData, setBattleData] = useState(null);
  const [aggregatedStats, setAggregatedStats] = useState(null);
  const [configData, setConfigData] = useState(null);

  const isDataLoading = !userDetails || isLoadingUserDetails || isRecapLoading;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const configResponse = await callBackbonePostApi({
          action: "getConfig"
        }, '');
        
        if (configResponse?.status === 200 && configResponse?.data) {
          setConfigData(configResponse.data);
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      }

      if (!!sessionId) {
        try {
          const response = await callBackbonePostApi({
            sessionId: sessionId,
            filterByUserDataOnly: true,
            action: "getDetailedBattleLog"
          }, '');
          
          setIsLoading(false);

          if (response?.status === 200 && response?.data) {
            setBattleData(response?.data);
            processAggregatedStats(response?.data);
          }
        } catch (error) {
          console.error('Error fetching battle log:', error);
          setIsLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const calculateRepCount = (repCountArray) => {
    if (!repCountArray?.length || !Array.isArray(repCountArray)) return 0;
    return repCountArray.reduce((sum, count) => (sum ?? 0) + (count ?? 0), 0);
  };

  const processSpanWeight = (spanWeightArray, repCount, exerciseStat) => {
    if (!spanWeightArray?.length || !Array.isArray(spanWeightArray) || repCount <= 0) {
      return false;
    }
    
    spanWeightArray.forEach((spanWeight, index) => {
      const weight = Number(spanWeight);
      if (weight > 0 && index < repCount) {
        const totalWeight = weight * 2;
        exerciseStat.individualRepWeights.push(totalWeight);
        exerciseStat.maxWeight = Math.max(exerciseStat.maxWeight, totalWeight);
        exerciseStat.totalWeight += totalWeight;
        exerciseStat.totalWeightedReps += 1;
      }
    });
    
    return true;
  };

  const processSetWeight = (setWeight, repCount, exerciseStat) => {
    const weight = Number(setWeight);
    if (weight <= 0 || repCount <= 0) return false;
    
    exerciseStat.weights.push(weight);
    
    for (let i = 0; i < repCount; i++) {
      exerciseStat.individualRepWeights.push(weight);
    }
    
    exerciseStat.maxWeight = Math.max(exerciseStat.maxWeight, weight);
    exerciseStat.totalWeight += (weight * repCount);
    exerciseStat.totalWeightedReps += repCount;
    
    return true;
  };

  const processExerciseEvent = (event, stats) => {
    const exerciseName = String(event?.Exercise ?? 'Unknown');

    if (!stats.exerciseStats[exerciseName]) {
      stats.exerciseStats[exerciseName] = {
        sets: 0,
        totalReps: 0,
        totalVolume: 0,
        totalWork: 0,
        totalPower: 0,
        avgRepsPerSet: 0,
        weights: [],
        ormValues: [],
        maxWeight: 0,
        totalWeight: 0,
        totalWeightedReps: 0,
        individualRepWeights: []
      };
    }
    
    const exerciseStat = stats.exerciseStats[exerciseName];
    exerciseStat.sets += 1;
    
    const repCount = calculateRepCount(event?.RepCount);
    exerciseStat.totalReps += repCount;
    exerciseStat.totalVolume += Number(event?.SimpleVolume ?? 0);
    exerciseStat.totalWork += Number(event?.TotalWork ?? 0);
    exerciseStat.totalPower += Number(event?.TotalPower ?? 0);
    
    const spanWeightProcessed = processSpanWeight(event?.SpanWeight, repCount, exerciseStat);
    if (!spanWeightProcessed) {
      processSetWeight(event?.setWeight, repCount, exerciseStat);
    }
    
    if (event?.FinalORM != null && !isNaN(event.FinalORM)) {
      exerciseStat.ormValues.push(Number(event.FinalORM));
    }
    
    stats.totalSets += 1;
    stats.totalReps += repCount;
  };

  const calculateExerciseAverages = (exerciseStat) => {
    exerciseStat.avgRepsPerSet = exerciseStat.sets > 0 
      ? Math.round(exerciseStat.totalReps / exerciseStat.sets)
      : 0;
    
    exerciseStat.avgWeightPerRep = exerciseStat.totalReps > 0
      ? Number((exerciseStat.totalVolume / exerciseStat.totalReps).toFixed(1))
      : 0;
    
    exerciseStat.avgORM = exerciseStat.ormValues?.length > 0
      ? Number(((exerciseStat.ormValues.reduce((sum, value) => (sum ?? 0) + (value ?? 0), 0) / exerciseStat.ormValues.length) * 2).toFixed(1))
      : 0;
  };

  const logExerciseStats = (exercise, exerciseStat) => {
    // Stats logging removed for cleaner console
  };

  const processAggregatedStats = (data) => {
    try {
      if (!data?.events?.length || !Array.isArray(data.events)) {
        setAggregatedStats(null);
        return;
      }

      const cleanData = {
        ...data,
        events: data.events.filter(event => event && typeof event === 'object')
      };

      const stats = {
        exerciseStats: {},
        totalSets: 0,
        totalReps: 0,
        powerUpsUsed: 0,
        sidekicksDeployed: 0
      };

      cleanData.events.forEach(event => {
        if (!event) return;
        
        if (event.Exercise && event.UserSet === true) {
          processExerciseEvent(event, stats);
        }

        if (event.EntityType === "PowerUp") {
          stats.powerUpsUsed += 1;
        }
        if (event.EntityType === "Pet") {
          stats.sidekicksDeployed += 1;
        }
      });

      Object.keys(stats.exerciseStats).forEach(exercise => {
        const exerciseStat = stats.exerciseStats[exercise];
        if (!exerciseStat) return;
        
        calculateExerciseAverages(exerciseStat);
        logExerciseStats(exercise, exerciseStat);
      });

      setAggregatedStats(stats);
    } catch (error) {
      console.error('Error processing battle stats:', error);
      setAggregatedStats(null);
    }
  };

  const getExerciseName = (exerciseCode) => {
    try {
      if (!configData?.ShortcodeToName || !exerciseCode) return exerciseCode ?? 'Unknown';
      
      const exercise = Object.values(configData.ShortcodeToName).find(ex => 
        ex.inGameName === exerciseCode
      );
      
      if (exercise) {
        return exercise.fullPrettyName ?? exercise.prettyName ?? exerciseCode;
      } else {
        return exerciseCode ?? 'Unknown';
      }
    } catch (error) {
      console.error('Error getting exercise name for:', exerciseCode, error);
      return exerciseCode ?? 'Unknown';
    }
  };

  const getLeagueName = (leagueNumber) => {
    try {
      if (!configData?.LeagueInformation || !leagueNumber) return `League ${leagueNumber ?? 'Unknown'}`;
      const league = configData.LeagueInformation.find(l => l.leagueNumber === Number(leagueNumber));
      return league?.leagueName ?? `League ${leagueNumber}`;
    } catch (error) {
      return `League ${leagueNumber ?? 'Unknown'}`;
    }
  };

  const formatTime = (seconds) => {
    const totalSeconds = Number(seconds ?? 0);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (num == null || isNaN(num)) return '0';
    return Number(num).toLocaleString();
  };

  const sumCoinsEarnedBreakout = (coinsEarnedBreakout) => {
    if (!coinsEarnedBreakout) return null;

    let values = coinsEarnedBreakout;
    if (typeof coinsEarnedBreakout === 'string') {
      try {
        values = JSON.parse(coinsEarnedBreakout);
      } catch {
        return null;
      }
    }

    if (values?.L && Array.isArray(values.L)) {
      values = values.L.map(item => item?.N ?? item?.n ?? item);
    }

    if (!Array.isArray(values) || values.length === 0) {
      return null;
    }

    return values.reduce((sum, val) => {
      const num = Number(val);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  };

  const sessionSummary = battleData?.sessionSummary ?? {};
  
  const safeSessionSummary = {
    SessionType: String(sessionSummary?.SessionType ?? 'N/A'),
    ChalName: String(sessionSummary?.ChalName ?? 'N/A'),
    League: String(sessionSummary?.League ?? 'N/A'),
    TotalGPSecs: Number(sessionSummary?.TotalGPSecs ?? 0),
    UserScore: Number(sessionSummary?.UserScore ?? 0),
    ChalScore: Number(sessionSummary?.ChalScore ?? 0),
    UserEarnedTrophies: Number(sessionSummary?.UserEarnedTrophies ?? 0),
    EndCoins: Number(sessionSummary?.EndCoins ?? 0),
    StartCoins: Number(sessionSummary?.StartCoins ?? 0),
    Calories: Number(sessionSummary?.Calories ?? 0),
    UserChampionsDeployed: Number(sessionSummary?.UserChampionsDeployed ?? 0),
    UserDestroyedCrystals: Number(sessionSummary?.UserDestroyedCrystals ?? 0),
    AIChampionsDeployed: Number(sessionSummary?.AIChampionsDeployed ?? 0),
    AIDestroyedCrystals: Number(sessionSummary?.AIDestroyedCrystals ?? 0),
    TotalWork: Number(sessionSummary?.TotalWork ?? 0),
    TotalVolume: Number(sessionSummary?.TotalVolume ?? 0),
    ChalLevel: Number(sessionSummary?.ChalLevel ?? 0),
    AchSummary: Array.isArray(sessionSummary?.AchSummary) ? sessionSummary.AchSummary : []
  };

  const coinsEarned =
    sumCoinsEarnedBreakout(sessionSummary?.CoinsEarnedBreakout) ??
    (safeSessionSummary.EndCoins - safeSessionSummary.StartCoins);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#1a1a1a' }}>
      <MetaTags
        title={`Battle Breakdown - ${username}`}
        description={`Detailed battle breakdown for ${username}'s workout session`}
      />

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Box sx={{ 
          mt: 3, 
          mb: 4,
          p: { xs: 3, md: 4 },
          background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 133, 51, 0.1) 100%)',
          borderRadius: 3,
          border: '1px solid rgba(255, 107, 0, 0.3)',
          boxShadow: '0 8px 32px rgba(255, 107, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap'
        }}>
          <CustomButton
            onClick={() => navigate(`/member/${username}/workouts/${sessionId}`)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              backgroundColor: 'rgba(255, 107, 0, 0.2)',
              border: '1px solid rgba(255, 107, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 0, 0.3)'
              }
            }}
          >
            <ArrowBackIcon /> Back to Workout
          </CustomButton>
          {canGiveFeedback && (
            <CustomButton
              onClick={() => navigate(`/feedback/session/${sessionId}`)}
              sx={{
                backgroundColor: 'rgba(255, 107, 0, 0.2)',
                border: '1px solid rgba(255, 107, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 0, 0.3)'
                }
              }}
            >
              Give Feedback
            </CustomButton>
          )}
          <CustomButton
            onClick={() => navigate('/feedback/topic/battleBreakdown')}
            sx={{
              backgroundColor: 'rgba(0, 118, 255, 0.15)',
              border: '1px solid rgba(0, 118, 255, 0.45)',
              '&:hover': {
                backgroundColor: 'rgba(0, 118, 255, 0.25)'
              }
            }}
          >
            Feedback on this page
          </CustomButton>
          <Typography variant="h3" sx={{ 
            color: '#fff', 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff6b00, #ff8533)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            ⚔️ Full Battle Breakdown
          </Typography>
        </Box>

        {isDataLoading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 4
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              border: '4px solid rgba(255, 107, 0, 0.3)',
              borderTop: '4px solid #ff6b00',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
            <Typography variant="h5" sx={{ 
              color: '#fff', 
              textAlign: 'center',
              background: 'linear-gradient(45deg, #ff6b00, #ff8533)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Loading Battle Data...
            </Typography>
            <Typography variant="body1" sx={{ 
              color: '#888', 
              textAlign: 'center',
              maxWidth: 400
            }}>
              Analyzing your workout performance and battle statistics. This may take a moment.
            </Typography>
          </Box>
        ) : (
          <>

            <Box sx={{ 
              mt: 4, 
              p: { xs: 3, md: 4 }, 
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
              borderRadius: 3,
              border: '1px solid #333',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <Typography variant="h4" sx={{ 
                color: '#fff', 
                mb: 3, 
                fontWeight: 'bold',
                textAlign: 'center',
                background: 'linear-gradient(45deg, #ff6b00, #ff8533)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Session Overview
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Battle Type</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{safeSessionSummary.SessionType}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Challenger</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{safeSessionSummary.ChalName}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>League</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{getLeagueName(safeSessionSummary.League)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Duration</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{formatTime(safeSessionSummary.TotalGPSecs)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Final Score</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {formatNumber(safeSessionSummary.UserScore)} - {formatNumber(safeSessionSummary.ChalScore)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Trophies Earned</Typography>
                    <Typography sx={{ color: safeSessionSummary.UserEarnedTrophies < 0 ? '#ff4444' : '#00ff00', fontWeight: 'bold' }}>
                      {safeSessionSummary.UserEarnedTrophies >= 0 ? '+' : ''}{formatNumber(safeSessionSummary.UserEarnedTrophies)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Coins Earned</Typography>
                    <Typography sx={{ color: '#ffd700', fontWeight: 'bold' }}>
                      {formatNumber(coinsEarned)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Calories Burned</Typography>
                    <Typography sx={{ color: '#ff6b00', fontWeight: 'bold' }}>{formatNumber(safeSessionSummary.Calories)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {aggregatedStats?.exerciseStats && Object.keys(aggregatedStats.exerciseStats).length > 0 && (
              <Box sx={{ 
                mt: 4, 
                p: { xs: 3, md: 4 }, 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
                borderRadius: 3,
                border: '1px solid #333',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  color: '#fff', 
                  mb: 3, 
                  fontWeight: 'bold',
                  textAlign: 'center',
                  background: 'linear-gradient(45deg, #00ff00, #66ff66)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Exercise Performance
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(aggregatedStats.exerciseStats).map(([exercise, stats]) => (
                    <Grid item xs={12} md={6} key={exercise}>
                      <Box sx={{ 
                        backgroundColor: 'rgba(0, 255, 0, 0.1)', 
                        p: 3, 
                        borderRadius: 3,
                        border: '1px solid rgba(0, 255, 0, 0.3)',
                        boxShadow: '0 4px 16px rgba(0, 255, 0, 0.1)'
                      }}>
                        <Typography variant="h6" sx={{ 
                          color: '#00ff00', 
                          mb: 3, 
                          fontWeight: 'bold',
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}>
                          {getExerciseName(exercise)}
                        </Typography>
                                                <Box sx={{ mb: 3 }}>
                          {/* Primary Stats - Most Important */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0,
                            mb: 2
                          }}>
                            <Box sx={{ 
                              p: 3, 
                              backgroundColor: '#000000',
                              flex: 1,
                              minWidth: 120,
                              textAlign: 'center',
                              borderRight: '1px solid #333'
                            }}>
                              <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Sets</Typography>
                              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.5rem' }}>{formatNumber(stats.sets)}</Typography>
                            </Box>
                            <Box sx={{ 
                              p: 3, 
                              backgroundColor: '#141516',
                              flex: 1,
                              minWidth: 120,
                              textAlign: 'center',
                              borderRight: '1px solid #333'
                            }}>
                              <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Reps</Typography>
                              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.5rem' }}>{formatNumber(stats.totalReps)}</Typography>
                            </Box>
                            <Box sx={{ 
                              p: 3, 
                              backgroundColor: '#000000',
                              flex: 1,
                              minWidth: 120,
                              textAlign: 'center',
                              borderRight: '1px solid #333'
                            }}>
                              <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Avg Reps/Set</Typography>
                              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.5rem' }}>{formatNumber(stats.avgRepsPerSet)}</Typography>
                            </Box>
                            <Box sx={{ 
                              p: 3, 
                              backgroundColor: '#141516',
                              flex: 1,
                              minWidth: 120,
                              textAlign: 'center',
                              borderRight: '1px solid #333'
                            }}>
                              <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Weight/Rep</Typography>
                              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.5rem' }}>{formatNumber(stats.avgWeightPerRep)} lbs</Typography>
                            </Box>
                            <Box sx={{ 
                              p: 3, 
                              backgroundColor: '#000000',
                              flex: 1,
                              minWidth: 120,
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Volume</Typography>
                              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.5rem' }}>{formatNumber(Math.round(stats.totalVolume))} lbs</Typography>
                            </Box>
                          </Box>

                          {/* Secondary Stats - Simple List */}
                          <Box sx={{ 
                            backgroundColor: '#141516',
                            border: '1px solid #333',
                            borderRadius: 1
                          }}>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: '#000000',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              borderBottom: '1px solid #333',
                              '&:hover': { backgroundColor: '#1a1a1a' }
                            }}
                              onClick={() => {
                                const element = document.getElementById(`details-${exercise.replace(/\s+/g, '-')}`);
                                if (element) {
                                  element.style.display = element.style.display === 'none' || element.style.display === '' ? 'block' : 'none';
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#888', fontSize: '14px', fontWeight: 500 }}>
                                Additional Stats & Details
                              </Typography>
                              <Typography sx={{ color: '#666', fontSize: '12px' }}>▼</Typography>
                            </Box>
                            <Box 
                              id={`details-${exercise.replace(/\s+/g, '-')}`}
                              sx={{ 
                                display: 'none',
                                p: 2
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2
                              }}>
                                <Box sx={{ 
                                  p: 2, 
                                  backgroundColor: '#000000', 
                                  borderRadius: 1,
                                  border: '1px solid #333',
                                  flex: 1,
                                  minWidth: 150,
                                  textAlign: 'center'
                                }}>
                                  <Typography variant="caption" sx={{ color: '#888', mb: 1, fontSize: '11px', textTransform: 'uppercase' }}>Max Weight Used</Typography>
                                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(Math.round(stats.maxWeight))} lbs</Typography>
                                </Box>
                                <Box sx={{ 
                                  p: 2, 
                                  backgroundColor: '#141516', 
                                  borderRadius: 1,
                                  border: '1px solid #333',
                                  flex: 1,
                                  minWidth: 150,
                                  textAlign: 'center'
                                }}>
                                  <Typography variant="caption" sx={{ color: '#888', mb: 1, fontSize: '11px', textTransform: 'uppercase' }}>One-Rep Max (ORM)</Typography>
                                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(stats.avgORM)} lbs</Typography>
                                </Box>
                                <Box sx={{ 
                                  p: 2, 
                                  backgroundColor: '#000000', 
                                  borderRadius: 1,
                                  border: '1px solid #333',
                                  flex: 1,
                                  minWidth: 150,
                                  textAlign: 'center'
                                }}>
                                  <Typography variant="caption" sx={{ color: '#888', mb: 1, fontSize: '11px', textTransform: 'uppercase' }}>Total Work</Typography>
                                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(Math.round(stats.totalWork))} ft-lbs</Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}



            <Box sx={{ 
              mt: 4, 
              p: { xs: 3, md: 4 }, 
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
              borderRadius: 3,
              border: '1px solid #333',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <Typography variant="h4" sx={{ 
                color: '#fff', 
                mb: 3, 
                fontWeight: 'bold',
                textAlign: 'center',
                background: 'linear-gradient(45deg, #ff6b00, #ff8533)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Battle Summary
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: 'rgba(0, 255, 0, 0.1)', 
                    p: 3, 
                    borderRadius: 3,
                    border: '1px solid rgba(0, 255, 0, 0.3)',
                    boxShadow: '0 4px 16px rgba(0, 255, 0, 0.1)'
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: '#00ff00', 
                      mb: 3, 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Your Performance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Champions Deployed</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(safeSessionSummary.UserChampionsDeployed)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Crystals Destroyed</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(safeSessionSummary.UserDestroyedCrystals)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Total Work</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(Math.round(safeSessionSummary.TotalWork))} ft-lbs
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Total Volume</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(Math.round(safeSessionSummary.TotalVolume))} lbs
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: 'rgba(255, 0, 0, 0.1)', 
                    p: 3, 
                    borderRadius: 3,
                    border: '1px solid rgba(255, 0, 0, 0.3)',
                    boxShadow: '0 4px 16px rgba(255, 0, 0, 0.1)'
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: '#ff0000', 
                      mb: 3, 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Opponent Performance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Champions Deployed</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(safeSessionSummary.AIChampionsDeployed)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Crystals Destroyed</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(safeSessionSummary.AIDestroyedCrystals)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Challenger Level</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(Math.round(safeSessionSummary.ChalLevel))}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Challenger Score</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {formatNumber(safeSessionSummary.ChalScore)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {safeSessionSummary.AchSummary && safeSessionSummary.AchSummary.length > 0 && (
              <Box sx={{ 
                mt: 4, 
                p: { xs: 3, md: 4 }, 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
                borderRadius: 3,
                border: '1px solid #333',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  color: '#fff', 
                  mb: 3, 
                  fontWeight: 'bold',
                  textAlign: 'center',
                  background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  🏆 Achievements Earned
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                  {safeSessionSummary.AchSummary.map((achievement, index) => {
                                          const name = String(achievement?.Name ?? 'Achievement');
                    const reward = Number(achievement?.RewardVal ?? 0);
                    return (
                      <Chip
                        key={index}
                        label={`${name} (+${reward} coins)`}
                        sx={{
                          backgroundColor: 'rgba(255, 215, 0, 0.2)',
                          color: '#ffd700',
                          fontSize: '1rem',
                          padding: '20px 10px',
                          border: '1px solid rgba(255, 215, 0, 0.5)',
                          borderRadius: 3,
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 215, 0, 0.3)',
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {(aggregatedStats?.powerUpsUsed > 0 || aggregatedStats?.sidekicksDeployed > 0) && (
              <Box sx={{ 
                mt: 4, 
                p: { xs: 3, md: 4 }, 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
                borderRadius: 3,
                border: '1px solid #333',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  color: '#fff', 
                  mb: 3, 
                  fontWeight: 'bold',
                  textAlign: 'center',
                  background: 'linear-gradient(45deg, #9c27b0, #e1bee7)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  ⚡ Special Items Used
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                  {aggregatedStats?.powerUpsUsed > 0 && (
                    <Grid item xs={6} md={3}>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: 'rgba(156, 39, 176, 0.1)', 
                        borderRadius: 3,
                        border: '1px solid rgba(156, 39, 176, 0.3)',
                        boxShadow: '0 4px 16px rgba(156, 39, 176, 0.1)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Power-ups Used</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>
                          {formatNumber(aggregatedStats.powerUpsUsed)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {aggregatedStats?.sidekicksDeployed > 0 && (
                    <Grid item xs={6} md={3}>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: 'rgba(156, 39, 176, 0.1)', 
                        borderRadius: 3,
                        border: '1px solid rgba(156, 39, 176, 0.3)',
                        boxShadow: '0 4px 16px rgba(156, 39, 176, 0.1)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Sidekicks Deployed</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>
                          {formatNumber(aggregatedStats.sidekicksDeployed)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
};

const BattleBreakdownWithErrorBoundary = () => (
  <BattleBreakdownErrorBoundary>
    <BattleBreakdown />
  </BattleBreakdownErrorBoundary>
);

export default BattleBreakdownWithErrorBoundary;
